import { useEffect, useState } from 'react';

interface WhaleAnalysisProps {
  refreshInterval?: number;
}

interface Holder {
  address: string;
  chainId: number;
  chain: string;
  balance: number;
  lastUpdated: string | null;
}

interface ChainSummary {
  chainId: number;
  chain: string;
  holders: number;
  totalBalance: number;
}

interface Distribution {
  gini: number | null;
  herfindahl: number | null;
  totalHolders: number;
  top1Pct: number;
  top5Pct: number;
  top10Pct: number;
  top25Pct: number;
  whaleCount: number;
  whaleShare: number;
  largeCount: number;
  largeShare: number;
  mediumCount: number;
  mediumShare: number;
  smallCount: number;
  smallShare: number;
  totalSupply: number;
  timestamp: string | null;
}

interface HistoryPoint {
  time: string;
  gini: number | null;
  herfindahl: number | null;
  totalHolders: number;
  whaleCount: number;
  whaleShare: number | null;
  top10Pct: number | null;
}

export function WhaleAnalysis({ refreshInterval = 60000 }: WhaleAnalysisProps) {
  const [topHolders, setTopHolders] = useState<Holder[]>([]);
  const [chains, setChains] = useState<ChainSummary[]>([]);
  const [distribution, setDistribution] = useState<Distribution | null>(null);
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historyHours, setHistoryHours] = useState(168);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval, historyHours]);

  const loadData = async () => {
    try {
      const [analysis, hist] = await Promise.all([
        fetchJson('/whale-analysis'),
        fetchJson(`/whale-analysis/history?hours=${historyHours}`),
      ]);
      setTopHolders(analysis.topHolders || []);
      setChains(analysis.chains || []);
      setDistribution(analysis.distribution || null);
      setHistory(hist.history || []);
      setLoading(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load whale data');
      setLoading(false);
    }
  };

  if (loading && !distribution) {
    return <div className="whale-analysis loading">Loading whale analysis...</div>;
  }

  if (error) {
    return <div className="whale-analysis error">{error}</div>;
  }

  const fmtNum = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  const fmtPct = (n: number) => `${n.toFixed(1)}%`;
  const fmtAddr = (a: string) => `${a.slice(0, 6)}...${a.slice(-4)}`;
  const chainColor = (id: number) => {
    const colors: Record<number, string> = { 1: '#627eea', 56: '#f0b90b', 137: '#8247e5', 195: '#ff0013' };
    return colors[id] || '#888';
  };

  // Concentration bar segments
  const segments = distribution
    ? [
        { label: 'Whales', share: distribution.whaleShare, color: '#ff4d4d' },
        { label: 'Large', share: distribution.largeShare, color: '#ff8c42' },
        { label: 'Medium', share: distribution.mediumShare, color: '#4ecdc4' },
        { label: 'Small', share: distribution.smallShare, color: '#45b7d1' },
      ]
    : [];

  // SVG chart for whale share history
  const chartW = 600;
  const chartH = 140;
  const chartPad = { top: 10, right: 10, bottom: 20, left: 40 };
  const plotW = chartW - chartPad.left - chartPad.right;
  const plotH = chartH - chartPad.top - chartPad.bottom;

  const validHistory = history.filter((h) => h.whaleShare != null);
  const minShare = validHistory.length > 0 ? Math.min(...validHistory.map((h) => h.whaleShare!)) - 1 : 0;
  const maxShare = validHistory.length > 0 ? Math.max(...validHistory.map((h) => h.whaleShare!)) + 1 : 100;

  const historyPath =
    validHistory.length > 1
      ? validHistory
          .map((h, i) => {
            const x = chartPad.left + (i / (validHistory.length - 1)) * plotW;
            const y = chartPad.top + plotH - ((h.whaleShare! - minShare) / (maxShare - minShare)) * plotH;
            return `${i === 0 ? 'M' : 'L'}${x},${y}`;
          })
          .join(' ')
      : '';

  const holdersPath =
    validHistory.length > 1
      ? (() => {
          const minH = Math.min(...validHistory.map((h) => h.totalHolders));
          const maxH = Math.max(...validHistory.map((h) => h.totalHolders));
          const range = maxH - minH || 1;
          return validHistory
            .map((h, i) => {
              const x = chartPad.left + (i / (validHistory.length - 1)) * plotW;
              const y = chartPad.top + plotH - ((h.totalHolders - minH) / range) * plotH;
              return `${i === 0 ? 'M' : 'L'}${x},${y}`;
            })
            .join(' ');
        })()
      : '';

  return (
    <div className="whale-analysis">
      {/* Distribution Summary */}
      {distribution && (
        <div className="whale-summary">
          <div className="whale-metrics">
            <div className="metric-card">
              <span className="metric-label">Gini Coefficient</span>
              <span className="metric-value">{distribution.gini?.toFixed(3) ?? 'N/A'}</span>
              <span className="metric-sub">{distribution.gini && distribution.gini > 0.6 ? 'High concentration' : 'Moderate'}</span>
            </div>
            <div className="metric-card">
              <span className="metric-label">Total Holders</span>
              <span className="metric-value">{fmtNum(distribution.totalHolders)}</span>
              <span className="metric-sub">Across {chains.length} chains</span>
            </div>
            <div className="metric-card">
              <span className="metric-label">Whales</span>
              <span className="metric-value">{distribution.whaleCount}</span>
              <span className="metric-sub">{fmtPct(distribution.whaleShare)} of supply</span>
            </div>
            <div className="metric-card">
              <span className="metric-label">Top 10%</span>
              <span className="metric-value">{fmtPct(distribution.top10Pct)}</span>
              <span className="metric-sub">of total supply</span>
            </div>
          </div>

          {/* Concentration Bar */}
          <div className="concentration-bar-section">
            <h4>Supply Distribution</h4>
            <div className="concentration-bar">
              {segments.map((seg) => (
                <div
                  key={seg.label}
                  className="bar-segment"
                  style={{ width: `${seg.share}%`, backgroundColor: seg.color }}
                  title={`${seg.label}: ${fmtPct(seg.share)}`}
                >
                  {seg.share > 8 && <span>{seg.label}</span>}
                </div>
              ))}
            </div>
            <div className="bar-legend">
              {segments.map((seg) => (
                <span key={seg.label} className="legend-item">
                  <span className="legend-dot" style={{ backgroundColor: seg.color }} />
                  {seg.label}: {fmtPct(seg.share)}
                </span>
              ))}
            </div>
          </div>

          {/* Top % Shares */}
          <div className="top-shares">
            <h4>Concentration by Percentile</h4>
            <div className="shares-bars">
              {[
                { label: 'Top 1%', value: distribution.top1Pct },
                { label: 'Top 5%', value: distribution.top5Pct },
                { label: 'Top 10%', value: distribution.top10Pct },
                { label: 'Top 25%', value: distribution.top25Pct },
              ].map((item) => (
                <div key={item.label} className="share-row">
                  <span className="share-label">{item.label}</span>
                  <div className="share-bar-bg">
                    <div className="share-bar-fill" style={{ width: `${item.value}%` }} />
                  </div>
                  <span className="share-value">{fmtPct(item.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Whale Share History Chart */}
      {validHistory.length > 1 && (
        <div className="whale-history">
          <div className="history-header">
            <h4>Whale Share History</h4>
            <div className="history-range">
              {[24, 168, 720].map((h) => (
                <button
                  key={h}
                  className={`range-btn ${historyHours === h ? 'active' : ''}`}
                  onClick={() => setHistoryHours(h)}
                >
                  {h === 24 ? '24h' : h === 168 ? '7d' : '30d'}
                </button>
              ))}
            </div>
          </div>
          <svg viewBox={`0 0 ${chartW} ${chartH}`} className="whale-chart">
            {/* Y-axis labels */}
            <text x={chartPad.left - 5} y={chartPad.top + 4} textAnchor="end" className="axis-label">
              {maxShare.toFixed(0)}%
            </text>
            <text x={chartPad.left - 5} y={chartPad.top + plotH + 4} textAnchor="end" className="axis-label">
              {minShare.toFixed(0)}%
            </text>
            {/* Grid lines */}
            <line
              x1={chartPad.left} y1={chartPad.top + plotH / 2}
              x2={chartPad.left + plotW} y2={chartPad.top + plotH / 2}
              className="grid-line"
            />
            {/* Whale share line */}
            <path d={historyPath} fill="none" stroke="#ff4d4d" strokeWidth="2" />
            {/* Holders line (secondary) */}
            <path d={holdersPath} fill="none" stroke="#45b7d1" strokeWidth="1.5" strokeDasharray="4,3" />
            {/* X-axis time labels */}
            {validHistory.length > 0 && (
              <>
                <text x={chartPad.left} y={chartH - 2} className="axis-label">
                  {new Date(validHistory[0].time).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </text>
                <text x={chartPad.left + plotW} y={chartH - 2} textAnchor="end" className="axis-label">
                  {new Date(validHistory[validHistory.length - 1].time).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </text>
              </>
            )}
          </svg>
          <div className="chart-legend">
            <span className="legend-item"><span className="legend-line" style={{ borderColor: '#ff4d4d' }} /> Whale Share %</span>
            <span className="legend-item"><span className="legend-line dashed" style={{ borderColor: '#45b7d1' }} /> Total Holders</span>
          </div>
        </div>
      )}

      {/* Chain Breakdown */}
      <div className="chain-breakdown">
        <h4>By Chain</h4>
        <div className="chain-cards">
          {chains.map((c) => (
            <div key={c.chainId} className="chain-card" style={{ borderLeftColor: chainColor(c.chainId) }}>
              <span className="chain-name">{c.chain}</span>
              <span className="chain-holders">{c.holders} holders</span>
              <span className="chain-balance">{fmtNum(c.totalBalance)} FCO</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top Holders Table */}
      <div className="top-holders">
        <h4>Top 20 Holders</h4>
        <div className="holders-table">
          <div className="holders-header">
            <span>#</span>
            <span>Address</span>
            <span>Chain</span>
            <span>Balance (FCO)</span>
            <span>% Supply</span>
          </div>
          {topHolders.slice(0, 20).map((h, i) => {
            const totalSupply = distribution?.totalSupply || 1;
            const pct = (h.balance / totalSupply) * 100;
            return (
              <div key={`${h.chainId}-${h.address}`} className={`holder-row ${i < 3 ? 'top-3' : ''}`}>
                <span className="rank">{i + 1}</span>
                <span className="address" title={h.address}>{fmtAddr(h.address)}</span>
                <span className="chain-badge" style={{ color: chainColor(h.chainId) }}>{h.chain}</span>
                <span className="balance">{fmtNum(h.balance)}</span>
                <span className="pct">{pct > 0.01 ? fmtPct(pct) : '<0.1%'}</span>
              </div>
            );
          })}
        </div>
      </div>

      {distribution?.timestamp && (
        <div className="whale-updated">
          Last updated: {new Date(distribution.timestamp).toLocaleString()}
        </div>
      )}
    </div>
  );
}

async function fetchJson(path: string) {
  const base = import.meta.env.VITE_API_BASE || '/api';
  const res = await fetch(`${base}${path}`);
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}
